import React, { useState, useEffect, useRef } from 'react';
import { getCallById } from '../../services/callService';
import type { MysteryShopCall, CriteriaResult } from '../../types/mysteryShop';
import { getScoreColor, formatDateTime, getScoreLabel, generateTakeaway } from './utils';

// ── CategoryCard (collapsible score breakdown) ──────────────────────────────

interface CategoryCardProps {
  title: string;
  score: number;
  max: number;
  weight: number;
  criteria: CriteriaResult[];
}

const CategoryCard: React.FC<CategoryCardProps> = ({ title, score, max, weight, criteria }) => {
  const [expanded, setExpanded] = useState(false);
  const passed = criteria.filter(c => c.passed).length;
  const total = criteria.length;
  const percentage = (score / max) * 100;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h4 className="font-medium text-gray-800">{title}</h4>
              <span className="text-xs text-gray-400">{weight}%</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{passed} of {total} criteria met</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${percentage}%`, backgroundColor: getScoreColor(percentage) }}
              />
            </div>

            <div className="text-right min-w-[60px]">
              <span className="text-xl font-bold" style={{ color: getScoreColor(percentage) }}>
                {score}
              </span>
              <span className="text-gray-400">/{max}</span>
            </div>

            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-3 space-y-2">
            {criteria.map((item, idx) => {
              const isNA = !item.passed && item.result === 'N/A';
              return (
                <div key={idx} className="flex items-center gap-3">
                  <span className={item.passed ? 'text-green-500' : isNA ? 'text-gray-300' : 'text-red-400'}>
                    {item.passed ? '✓' : isNA ? 'N/A' : '✗'}
                  </span>
                  <span className={`text-sm ${item.passed ? 'text-gray-700' : isNA ? 'text-gray-300' : 'text-gray-400'}`}>
                    {item.text}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── AudioPlayer ─────────────────────────────────────────────────────────────

const AudioPlayer: React.FC<{ src: string }> = ({ src }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying(!playing);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => {
          const audio = audioRef.current;
          if (audio) setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
        }}
        onLoadedMetadata={() => {
          const audio = audioRef.current;
          if (audio) setDuration(audio.duration);
        }}
        onEnded={() => setPlaying(false)}
      />
      <div className="flex items-center gap-4">
        <button
          onClick={togglePlay}
          className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 transition-colors flex-shrink-0"
        >
          {playing ? (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <rect x="3" y="2" width="4" height="12" />
              <rect x="9" y="2" width="4" height="12" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 2l10 6-10 6V2z" />
            </svg>
          )}
        </button>
        <div
          className="flex-1 cursor-pointer"
          onClick={(e) => {
            const audio = audioRef.current;
            if (!audio || !audio.duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            audio.currentTime = pct * audio.duration;
          }}
        >
          <div className="h-1.5 bg-gray-100 rounded-full">
            <div
              className="h-1.5 bg-gray-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <span className="text-xs text-gray-400">{duration ? formatTime(duration) : '—:——'}</span>
      </div>
    </div>
  );
};

// ── CallDetail ──────────────────────────────────────────────────────────────

interface CallDetailProps {
  callId: string;
}

export const CallDetail: React.FC<CallDetailProps> = ({ callId }) => {
  const [call, setCall] = useState<MysteryShopCall | null>(null);
  const [rev, setRev] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    getCallById(callId).then(setCall);
  }, [callId, rev]);

  if (!call) return null;

  const scoreLabel = getScoreLabel(call.overallScore);

  return (
    <div className="space-y-6">
      {/* Header + Score Hero */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{call.propertyName}</h1>
            <p className="text-gray-500 text-sm mt-1">
              {formatDateTime(call.date)} &bull; {call.agentName || 'Unknown Agent'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {call.tourInvited ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium text-green-700">Tour Invited</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-full">
                <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-sm font-medium text-red-700">No Tour</span>
              </div>
            )}
            <button
              onClick={() => setRev(r => r + 1)}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6"/>
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                <path d="M3 22v-6h6"/>
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Score hero */}
        <div className="mt-6 flex items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-medium">Overall Score</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span
                className="text-6xl font-bold tracking-tight"
                style={{ color: getScoreColor(call.overallScore) }}
              >
                {call.overallScore}
              </span>
              <span className="text-2xl text-gray-300 font-light">/100</span>
            </div>
          </div>

          <div
            className="px-3 py-1 rounded-full text-sm font-medium mb-2"
            style={{ backgroundColor: scoreLabel.bg, color: scoreLabel.color }}
          >
            {scoreLabel.text}
          </div>
        </div>

        {/* Takeaway */}
        <p className="mt-4 text-gray-600 text-sm border-t border-gray-100 pt-4">
          <span className="font-medium">Summary:</span> {generateTakeaway(call)}
        </p>
      </div>

      {/* Audio Player */}
      <AudioPlayer src={call.recordingUrl} />

      {/* Performance Breakdown */}
      <div>
        <h2 className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-3">Performance Breakdown</h2>

        <div className="space-y-3">
          {[...call.scores].sort((a, b) => a.order - b.order).map((cat, i) =>
            cat.highlighted ? (
              <div
                key={i}
                className={`p-4 rounded-xl border ${
                  call.tourInvited
                    ? 'bg-green-50 border-green-100'
                    : 'bg-red-50 border-red-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-medium ${call.tourInvited ? 'text-green-800' : 'text-red-800'}`}>
                      {cat.title}
                    </h4>
                    <p className={`text-sm mt-0.5 ${call.tourInvited ? 'text-green-600' : 'text-red-600'}`}>
                      {cat.weight}% of total score{!call.tourInvited && ' — critical miss'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-2xl font-bold ${call.tourInvited ? 'text-green-500' : 'text-red-500'}`}>
                      {cat.score}
                    </span>
                    <span className={call.tourInvited ? 'text-green-300' : 'text-red-300'}>
                      /{cat.max}
                    </span>
                  </div>
                </div>
              </div>
            ) : cat.criteria ? (
              <CategoryCard
                key={i}
                title={cat.title}
                score={cat.score}
                max={cat.max}
                weight={cat.weight}
                criteria={cat.criteria}
              />
            ) : null
          )}
        </div>
      </div>

      {/* Collapsible Transcript */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
          onClick={() => setShowTranscript(!showTranscript)}
        >
          <h2 className="font-medium text-gray-800">Call Transcript</h2>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showTranscript ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showTranscript && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="bg-gray-50 rounded-lg p-4 text-sm mt-3 space-y-1.5">
              {call.transcript.split('\n').filter(Boolean).map((line, i) => {
                const isUser = line.startsWith('User: ');
                return (
                  <p key={i} className={isUser ? 'text-black' : 'text-gray-800 italic'}>
                    {line}
                  </p>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
